/**
 * @file Public landing page — pages/Landing/LandingPage.tsx.
 */

import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LandingPage.css";
import bgImage from "../../../public/assets/landing_page_bg.png"; // adjust path
import { landingService } from "../../services/landingService";

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    datasetsCount: 0,
    materialsCoveredCount: 0,
    publicationSourceCounts: {
      peerReviewedScientificLiterature: 0,
      internalTechnicalReports: 0,
      epdsAndLcas: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await landingService.getStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch landing stats:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Hero Section */}
      <section
        className="landing-hero"
        style={{
          backgroundImage: `
      /* 👇 The gradient now uses #C7C7C7 for both transparent & solid ends */
      linear-gradient(to right, rgba(201,201,201,0) 68%, #C7C7C7 68%),
      url(${process.env.PUBLIC_URL}/assets/landing_page_bg.png)
    `,
          backgroundSize: "contain", // ✅ show full image, no cropping
          backgroundPosition: "left center", // ✅ image aligned properly
          backgroundRepeat: "no-repeat", // ✅ prevent image repetition
          backgroundColor: "#C7C7C7",
        }}
      >
        <div className="landing-overlay">
          <div className="landing-card">
            <h2>ICoMEC</h2>
            <p>Indian Construction Materials Embodied Carbon Data Base</p>

            <div className="btn-group">
              <button
                className="btn primary"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </button>
              <button
                className="btn secondary"
                onClick={() => navigate("/login")}
              >
                Log In
              </button>
            </div>

            <div className="landing-card-footer">
              <h3>CARBSE</h3>
              <p>Center for Advanced Research in Building Science Energy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="landing-info">
        <h3>Embodied Carbon Data for All.</h3>
        <p>
          ICoMEC dashboard is a free, open-source Embodied Carbon Data platform
          for Indian construction materials.
        </p>
      </section>

      {/* Grey Cards Section */}
      <section className="landing-cards">
        <div className="card">
           <img
            src="/images/CARBSE_EC Database_LandingPage_Data Collection_06Oct2025.png"
            alt="ICoMEC Data Collection"
            className="card-image"
          />
         
          <h4>ICoMEC Database</h4>
          <p>
            Built for collecting Embodied Carbon data for Indian construction
            materials. The database features a consistent, structured data
            collection template.
          </p>
          <button onClick={() => navigate("/login?role=CONTRIBUTOR")}>Explore more</button>
        </div>

        <div className="card">
          <img
            src="/images/CARBSE_EC Database_LandingPage_data visualization_06Oct2025.png"
            alt="ICoMEC Database Visualization"
            className="card-image"
          />
          <h4>ICoMEC Dashboard</h4>
          <p>
            ICoMEC dashboard provides you with the cradle-to-gate Embodied
            Carbon values for building construction materials.
          </p>
          <button onClick={() => navigate("/login?role=PUBLIC")}>Explore more</button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="landing-stats">
        <div className="card small-card">
          <h2>{loading ? "..." : stats.datasetsCount}</h2>
          <p>Datasets</p>
        </div>
        <div className="card small-card">
          <h2>{loading ? "..." : stats.materialsCoveredCount}</h2>
          <p>Materials Covered</p>
        </div>
      </section>

      {/* Data Extracted Section */}
      <section className="landing-data">
        <h3>Data extracted from</h3>
        <div className="data-grid">
          <div>
            <h2>
              {loading
                ? "..."
                : stats.publicationSourceCounts.peerReviewedScientificLiterature}
            </h2>
            <p>
              Peer-reviewed <br /> Scientific Literature
            </p>
          </div>
          <div>
            <h2>
              {loading
                ? "..."
                : stats.publicationSourceCounts.internalTechnicalReports}
            </h2>
            <p>
              Internal Technical <br /> Reports
            </p>
          </div>
          <div>
            <h2>
              {loading
                ? "..."
                : stats.publicationSourceCounts.epdsAndLcas}
            </h2>
            <p>EPDs and LCAs</p>
          </div>
        </div>
      </section>

      {/* Footer */}

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom (outside main footer) */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Landing Page. */
export default LandingPage;
